// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.OtherSettings.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

/**
 * other settings model
 */

import { observable, action, computed } from 'mobx';
import { isNil } from 'lodash/fp';

import constants from 'logic/data/Izenda.OtherSettingsConstants';
import tracker from 'share/tracking/Izenda.Tracker';
import { TimeZoneOffset } from 'share/utils/Izenda.DateTimeUtils';
import commonMessages from 'share/messages/Izenda.CommonMessages';
import uiState from 'logic/Izenda.UiState';

@tracker
export default class OtherSettings {
  @observable tenantId = null;
  @observable modified = null;
  @observable alertMessage = commonMessages.EMPTY_MESSAGE;

  @observable sortColumnNameValue = false;
  @observable sortColumnNameDefaultValue = false;

  @observable trimTimeInJoinsValue = false;
  @observable trimTimeInJoinsDefaultValue = false;

  @observable timezoneForDataOffsetValue = 0;
  @observable timezoneForDataOffsetDefaultValue = 0;

  @observable timezoneForTimestampOffsetValue = 0;
  @observable timezoneForTimestampOffsetDefaultValue = 0;

  @observable convertNullToEmptyStringValue = false;
  @observable convertNullToEmptyStringDefaultValue = false;

  @observable encodeVariablesInCustomUrlValue = true;
  @observable encodeVariablesInCustomUrlDefaultValue = true;

  @observable showSchemaNameValue = false;
  @observable showSchemaNameDefaultValue = false;

  @observable showIntroductionTextValue = false;
  @observable showIntroductionTextDefaultValue = false;

  @observable introductionTextValue = null;
  @observable introductionTextDefaultValue = null;

  @observable sendToDiskPathValue = null;
  @observable sendToDiskPathDefaultValue = null;

  @observable excelDataFilesSystemPathValue = null;
  @observable excelDataFilesSystemPathDefaultValue = null;

  @observable commonFilterSettingValue = 0;
  @observable commonFilterSettingDefaultValue = 0;

  @observable inheritFilterSettingValue = 0;
  @observable inheritFilterSettingDefaultValue = 0;

  @observable multipleSortOnGridHeaderValue = false;
  @observable multipleSortOnGridHeaderDefaultValue = false;

  @observable showPreviewValue = true;
  @observable showPreviewDefaultValue = true;

  @observable hideReportHeaderAndFooterValue = false;
  @observable hideReportHeaderAndFooterDefaultValue = false;

  @observable repeatTableHeaderValue = false;
  @observable repeatTableHeaderDefaultValue = false;

  @observable maximumSubreportLevelValue = 1;
  @observable maximumSubreportLevelDefaultValue = 1;

  @observable calendarWeekStartDayValue = 1;
  @observable calendarWeekStartDayDefaultValue = 1;

  @computed
  get isAllowMultiSortDisplayed() {
    return isNil(uiState.tenantId);
  }

  @computed
  get canShowPreview() {
    return !uiState.tenantId && !uiState.onlyShowTenant;
  }

  /**
   * Read others setting detail data (mostly this data comes from API).
   * @param {Object} otherSettings
   */
  @action
  readOtherSettingsDetail(otherSettings: any): any {
    const defaultTimezoneForDataOffsetValue = 0;
    const defaultTimezoneForTimestampOffsetValue = 0;

    // ACTUAL VALUES
    this.sortColumnNameValue = otherSettings.sortColumnNameValue;
    this.trimTimeInJoinsValue = otherSettings.trimTimeInJoinsValue;
    this.timezoneForDataOffsetValue = TimeZoneOffset.formatTimeZone(
      parseInt(otherSettings.timezoneForDataOffsetValue) || defaultTimezoneForDataOffsetValue
    );
    this.timezoneForTimestampOffsetValue = TimeZoneOffset.formatTimeZone(
      parseInt(otherSettings.timezoneForTimestampOffsetValue) || defaultTimezoneForTimestampOffsetValue
    );
    this.convertNullToEmptyStringValue = otherSettings.convertNullToEmptyStringValue;
    this.encodeVariablesInCustomUrlValue = otherSettings.encodeVariablesInCustomUrlValue;
    this.showSchemaNameValue = otherSettings.showSchemaNameValue;
    this.showIntroductionTextValue = otherSettings.showIntroductionTextValue;
    this.introductionTextValue = otherSettings.introductionTextValue;
    this.sendToDiskPathValue = otherSettings.sendToDiskPathValue;
    this.excelDataFilesSystemPathValue = otherSettings.excelDataFilesSystemPathValue;
    this.commonFilterSettingValue = otherSettings.commonFilterSettingValue;
    this.inheritFilterSettingValue = otherSettings.inheritFilterSettingValue;
    this.multipleSortOnGridHeaderValue = otherSettings.multipleSortOnGridHeaderValue;
    this.showPreviewValue = otherSettings.showPreviewValue;
    this.hideReportHeaderAndFooterValue = otherSettings.hideReportHeaderAndFooterValue;
    this.repeatTableHeaderValue = otherSettings.repeatTableHeaderValue;
    this.maximumSubreportLevelValue = otherSettings.maximumSubreportLevelValue;
    this.calendarWeekStartDayValue = otherSettings.calendarWeekStartDayValue;
    // DEFAULT VALUES
    this.sortColumnNameDefaultValue = otherSettings.sortColumnNameDefaultValue;
    this.trimTimeInJoinsDefaultValue = otherSettings.trimTimeInJoinsDefaultValue;
    this.timezoneForDataOffsetDefaultValue = otherSettings.timezoneForDataOffsetDefaultValue;
    this.timezoneForTimestampOffsetDefaultValue = otherSettings.timezoneForTimestampOffsetDefaultValue;
    this.convertNullToEmptyStringDefaultValue = otherSettings.convertNullToEmptyStringDefaultValue;
    this.encodeVariablesInCustomUrlDefaultValue = otherSettings.encodeVariablesInCustomUrlDefaultValue;
    this.showSchemaNameDefaultValue = otherSettings.showSchemaNameDefaultValue;
    this.showIntroductionTextDefaultValue = otherSettings.showIntroductionTextDefaultValue;
    this.introductionTextDefaultValue = otherSettings.introductionTextDefaultValue;
    this.sendToDiskPathDefaultValue = otherSettings.sendToDiskPathDefaultValue;
    this.excelDataFilesSystemPathDefaultValue = otherSettings.excelDataFilesSystemPathDefaultValue;
    this.commonFilterSettingDefaultValue = otherSettings.commonFilterSettingDefaultValue;
    this.inheritFilterSettingDefaultValue = otherSettings.inheritFilterSettingDefaultValue;
    this.multipleSortOnGridHeaderDefaultValue = otherSettings.multipleSortOnGridHeaderDefaultValue;
    this.showPreviewDefaultValue = otherSettings.showPreviewDefaultValue;
    this.hideReportHeaderAndFooterDefaultValue = otherSettings.hideReportHeaderAndFooterDefaultValue;
    this.repeatTableHeaderDefaultValue = otherSettings.repeatTableHeaderDefaultValue;
    this.maximumSubreportLevelDefaultValue = otherSettings.maximumSubreportLevelDefaultValue;
    this.calendarWeekStartDayDefaultValue = otherSettings.calendarWeekStartDayDefaultValue;
    this.tenantId = otherSettings.tenantId || null;
    this.modified = otherSettings.modified;

    this.resetDirty();
    return null;
  }

  /**
   * Reset value
   * @param {string} key
   */
  @action
  resetValue(key: string): void {
    switch (key) {
      case 'sortColumnName':
        this.sortColumnNameValue = this.sortColumnNameDefaultValue;
        break;
      case 'trimTimeInJoins':
        this.trimTimeInJoinsValue = this.trimTimeInJoinsDefaultValue;
        break;
      case 'timezoneForDataOffset':
        this.timezoneForDataOffsetValue = this.timezoneForDataOffsetDefaultValue;
        break;
      case 'timezoneForTimestampOffset':
        this.timezoneForTimestampOffsetValue = this.timezoneForTimestampOffsetDefaultValue;
        break;
      case 'convertNullToEmptyString':
        this.convertNullToEmptyStringValue = this.convertNullToEmptyStringDefaultValue;
        break;
      case 'encodeVariablesInCustomUrl':
        this.encodeVariablesInCustomUrlValue = this.encodeVariablesInCustomUrlDefaultValue;
        break;
      case 'showSchemaName':
        this.showSchemaNameValue = this.showSchemaNameDefaultValue;
        break;
      case 'showIntroductionText':
        this.showIntroductionTextValue = this.showIntroductionTextDefaultValue;
        break;
      case 'introductionText':
        this.introductionTextValue = this.introductionTextDefaultValue;
        break;
      case 'sendToDiskPath':
        this.sendToDiskPathValue = this.sendToDiskPathDefaultValue;
        break;
      case 'excelDataFilesSystemPath':
        this.excelDataFilesSystemPathValue = this.excelDataFilesSystemPathDefaultValue;
        break;
      case 'commonFilterSetting':
        this.commonFilterSettingValue = this.commonFilterSettingDefaultValue;
        break;
      case 'inheritFilterSetting':
        this.inheritFilterSettingValue = this.inheritFilterSettingDefaultValue;
        break;
      case 'multipleSortOnGridHeader':
        this.multipleSortOnGridHeaderValue = this.multipleSortOnGridHeaderDefaultValue;
        break;
      case 'showPreview':
        this.showPreviewValue = this.showPreviewDefaultValue;
        break;
      case 'hideReportHeaderAndFooter':
        this.hideReportHeaderAndFooterValue = this.hideReportHeaderAndFooterDefaultValue;
        break;
      case 'repeatTableHeader':
        this.repeatTableHeaderValue = this.repeatTableHeaderDefaultValue;
        break;
      case 'maximumSubreportLevel':
        this.maximumSubreportLevelValue = this.maximumSubreportLevelDefaultValue;
        break;
      case 'calendarWeekStartDay':
        this.calendarWeekStartDayValue = this.calendarWeekStartDayDefaultValue;
        break;
    }

    this.markDirty();
  }

  /**
   * Update value
   * @param {string} key
   * @param {Object} value
   */
  updateValue(key: string, value: any): void {
    const checkMaximumSubreportLevelOnTyping = (value: string): boolean => {
      var pattern = new RegExp(`^[${constants.MAXIMUM_SUBREPORT_LEVEL_MIN_VALUE}-${constants.MAXIMUM_SUBREPORT_LEVEL_MAX_VALUE}]$`);
      return value === '' || pattern.test(value);
    };

    switch (key) {
      case 'sortColumnName':
        this.sortColumnNameValue = value;
        break;
      case 'trimTimeInJoins':
        this.trimTimeInJoinsValue = value;
        break;
      case 'timezoneForDataOffset':
        if (TimeZoneOffset.checkTimeZoneFormatOnTyping(value)) {
          this.timezoneForDataOffsetValue = value;
        }
        break;
      case 'timezoneForTimestampOffset':
        if (TimeZoneOffset.checkTimeZoneFormatOnTyping(value)) {
          this.timezoneForTimestampOffsetValue = value;
        }
        break;
      case 'convertNullToEmptyString':
        this.convertNullToEmptyStringValue = value;
        break;
      case 'encodeVariablesInCustomUrl':
        this.encodeVariablesInCustomUrlValue = value;
        break;
      case 'showSchemaName':
        this.showSchemaNameValue = value;
        break;
      case 'showIntroductionText':
        this.showIntroductionTextValue = value;
        break;
      case 'introductionText':
        this.introductionTextValue = value;
        break;
      case 'sendToDiskPath':
        this.sendToDiskPathValue = value;
        break;
      case 'excelDataFilesSystemPath':
        this.excelDataFilesSystemPathValue = value;
        break;
      case 'commonFilterSetting':
        this.commonFilterSettingValue = parseInt(value);
        break;
      case 'inheritFilterSetting':
        this.inheritFilterSettingValue = parseInt(value);
        break;
      case 'multipleSortOnGridHeader':
        this.multipleSortOnGridHeaderValue = value;
        break;
      case 'showPreview':
        this.showPreviewValue = value;
        break;
      case 'hideReportHeaderAndFooter':
        this.hideReportHeaderAndFooterValue = value;
        break;
      case 'repeatTableHeader':
        this.repeatTableHeaderValue = value;
        break;
      case 'maximumSubreportLevel':
        if (checkMaximumSubreportLevelOnTyping(value)) {
          this.maximumSubreportLevelValue = parseInt(value) || '';
        }
        break;
      case 'calendarWeekStartDay':
        this.calendarWeekStartDayValue = parseInt(value.value);
        break;
    }

    this.markDirty();
  }

  /**
   * Post update value
   * @param {string} key
   * @param {string} value
   */
  postUpdateValue(key: string, value: string): void {
    switch (key) {
      case 'maximumSubreportLevel':
        this.maximumSubreportLevelValue = parseInt(value) || this.maximumSubreportLevelDefaultValue;
        break;
      case 'timezoneForDataOffset':
        this.timezoneForDataOffsetValue = TimeZoneOffset.formatTimeZone(parseInt(value) || this.timezoneForDataOffsetDefaultValue);
        break;
      case 'timezoneForTimestampOffset':
        this.timezoneForTimestampOffsetValue = TimeZoneOffset.formatTimeZone(parseInt(value) || this.timezoneForTimestampOffsetDefaultValue);
        break;
    }

    this.markDirty();
  }

  /**
   * Reset error message
   */
  resetErrorMessage(): void {
    this.alertMessage = commonMessages.EMPTY_MESSAGE;
  }
}



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.OtherSettings.js